<template>
  <div class="block pb-6">
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="BatchInquiryList" label="Batch Reports" :active="true" />
    </breadcrumb-list>

    <div class="container">
      <div class="box">
        <div class="level">
          <div class="level-left">
            <div>
              <h1 class="title">Batch Reports</h1>
              <p class="subtitle is-6 has-text-grey">
                Every batch you've run, most recent first. Open one to see its progress and
                results.
              </p>
            </div>
          </div>
          <div class="level-right">
            <div class="buttons">
              <b-button
                type="is-success"
                icon-left="account-multiple-check"
                :loading="runningAll"
                @click="runAllUnarchived"
              >
                Run all unarchived patients
              </b-button>
              <b-button
                type="is-light"
                icon-left="download"
                :disabled="batches.length === 0"
                @click="exportBatchInquiries"
              >
                Export
              </b-button>
            </div>
          </div>
        </div>

        <hr />

        <b-table
          :data="batches"
          :loading="loading"
          striped
          hoverable
          paginated
          per-page="15"
          @click="openBatchInquiry"
        >
          <b-table-column field="id" label="Batch" v-slot="props">#{{ props.row.id }}</b-table-column>

          <b-table-column field="status" label="Status" v-slot="props">
            <span class="tag" :class="statusClass(props.row.status)">{{ props.row.status }}</span>
          </b-table-column>

          <b-table-column field="progress" label="Progress" v-slot="props">
            {{ props.row.completed }} / {{ props.row.total }}
          </b-table-column>

          <b-table-column field="ran" label="Ran" v-slot="props">
            {{ ranCount(props.row) }}
          </b-table-column>

          <b-table-column field="inactive" label="Inactive" v-slot="props">
            {{ planCount(props.row, 'inactive') }}
          </b-table-column>

          <b-table-column field="unavailable" label="Unavailable" v-slot="props">
            {{ planCount(props.row, 'unavailable') }}
          </b-table-column>

          <b-table-column field="created_at" label="Created" v-slot="props">
            {{ formatDate(props.row.created_at) }}
          </b-table-column>

          <template #empty>
            <section class="section has-text-centered">
              <b-icon icon="file-alert" size="is-large"></b-icon>
              <p class="mt-2">You haven't run any batch reports yet.</p>
            </section>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../request';

export default {
  name: 'BatchInquiryList',
  data() {
    return {
      batches: [],
      loading: true,
      runningAll: false,
    };
  },
  created() {
    this.fetchBatchInquiries();
  },
  methods: {
    async fetchBatchInquiries() {
      try {
        const response = await request.get('/batch-inquiries');
        this.batches = response.data.batches || [];
      } catch {
        this.$buefy.toast.open({ message: 'Failed to load batch reports.', type: 'is-danger' });
      } finally {
        this.loading = false;
      }
    },
    openBatchInquiry(row) {
      this.$router.push({ name: 'BatchInquiryReport', params: { id: row.id } });
    },
    runAllUnarchived() {
      this.$buefy.dialog.confirm({
        title: 'Run all unarchived patients',
        message:
          'This re-runs every active (unarchived) patient — one re-run per patient — and uses ' +
          'one subscription unit each. Archive any patients you no longer track before running.',
        confirmText: 'Run all unarchived',
        type: 'is-success',
        onConfirm: async () => {
          this.runningAll = true;
          try {
            const response = await request.post('/batch-inquiries', { all_unarchived: true });
            this.$router.push({ name: 'BatchInquiryReport', params: { id: response.data.batch_id } });
          } catch (e) {
            const errors = (e.response && e.response.data && e.response.data.errors) || {};
            const msg = errors.NOFIELD || Object.values(errors)[0] || 'Failed to start the batch.';
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          } finally {
            this.runningAll = false;
          }
        },
      });
    },
    async exportBatchInquiries() {
      try {
        const response = await request.get('/batch-inquiries/export', { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        let filename = 'batch-reports.csv';
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+?)"?$/);
          if (match && match[1]) filename = match[1];
        }
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch {
        this.$buefy.toast.open({ message: 'Failed to export batch reports.', type: 'is-danger' });
      }
    },
    ranCount(batch) {
      return (batch.inquiries || []).filter((i) => i.status === 'complete').length;
    },
    planCount(batch, status) {
      return (batch.inquiries || []).filter((i) => i.plan_status === status).length;
    },
    statusClass(status) {
      return {
        'is-success': status === 'complete',
        'is-info': status === 'running',
        'is-light': status === 'pending',
        'is-danger': status === 'failed',
      };
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
    },
  },
};
</script>
